import {
  ComponentTreeNode,
  StreamingComponentTreeNode,
  FlexiblePageTreeNodeType,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { stripLinksFromRichText } from '../../richtext/ast';

/**
 * Recursively strip all links from Text nodes within a tree node and its children.
 * Handles streaming nodes where children or data properties may be undefined.
 *
 * @param node The tree node to process (may be undefined during streaming)
 * @returns A new node with links stripped, or undefined if input was undefined
 */
export function stripLinksFromTreeNode(node: ComponentTreeNode): ComponentTreeNode;
export function stripLinksFromTreeNode(node: StreamingComponentTreeNode): StreamingComponentTreeNode;
export function stripLinksFromTreeNode(node: undefined | null): undefined;
export function stripLinksFromTreeNode(
  node: ComponentTreeNode | StreamingComponentTreeNode | undefined | null,
): ComponentTreeNode | StreamingComponentTreeNode | undefined;
export function stripLinksFromTreeNode(
  node: ComponentTreeNode | StreamingComponentTreeNode | undefined | null,
): ComponentTreeNode | StreamingComponentTreeNode | undefined {
  // Guard for undefined/null nodes during streaming
  if (node == null) return undefined;

  let processedNode = node;

  // Check if this is a text node with RichText content that needs processing
  // During streaming, data.text might be undefined even for text nodes
  if (node.data?.type === FlexiblePageTreeNodeType.Text) {
    const textValue = (node.data as { text?: unknown } | undefined)?.text;
    if (textValue != null && typeof textValue !== 'string' && typeof textValue === 'object') {
      const strippedText = stripLinksFromRichText(textValue as Parameters<typeof stripLinksFromRichText>[0]);
      // Only update if we got a valid result (guards against undefined during streaming edge cases)
      if (strippedText != null) {
        processedNode = {
          ...node,
          data: {
            ...node.data,
            text: strippedText,
          } as typeof node.data,
        };
      }
    }
  }

  if ('children' in processedNode && Array.isArray(processedNode.children)) {
    return {
      ...processedNode,
      children: processedNode.children
        .filter((child): child is StreamingComponentTreeNode => child != null)
        .map((child) => stripLinksFromTreeNode(child)),
    };
  }

  return processedNode;
}
